import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { CheckIcon } from "@/components/icons/check-icon";
import { RoundedModal } from "@/components/modal/rounded-modal";
import { FilledButton } from "@/components/ui/buttons";
import { WEEKDAYS, WEEKDAY_LONG_LABELS, Weekday } from "@/features/meal-plan/constants/weekdays";
import { useRepeatMealOnSlots } from "@/hooks/requests/plan/use-repeat-meal-on-slots";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

interface RepeatMealModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  meal: Meal;
  mealTime: MealTime;
  /** Lowercase source weekday, e.g. "tuesday". */
  mealDay: string;
  targetDate?: string;
}

interface DayRowProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

const DayRow = memo(function DayRow({ label, checked, disabled = false, onToggle }: DayRowProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={checked}
      className={cn(
        "flex w-full items-center gap-3 py-3 text-left",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2",
          checked ? "border-[#620AD5] bg-[#620AD5]" : "border-[#620AD5] bg-transparent",
          disabled && "opacity-40",
        )}
      >
        {checked && <CheckIcon className="h-4 w-4 text-white" />}
      </span>
      <span
        className={cn(
          textVariants.bodySingleLine,
          "flex-1",
          disabled ? "text-gray-400" : "text-black",
        )}
      >
        {label}
      </span>
    </button>
  );
});

export const RepeatMealModal = memo(function RepeatMealModal({
  isOpen,
  onDismiss,
  meal,
  mealTime,
  mealDay,
  targetDate,
}: RepeatMealModalProps) {
  const sourceDay = mealDay.toLowerCase() as Weekday;
  const selectableDays = useMemo(() => WEEKDAYS.filter((d) => d !== sourceDay), [sourceDay]);
  const [selectedDays, setSelectedDays] = useState<Set<string>>(() => new Set());
  const { repeatMealOnSlotsAsync, isLoading } = useRepeatMealOnSlots();
  const { showInfo, showSuccess, showError } = useToast();

  // Reset selection each time the modal reopens.
  useEffect(() => {
    if (isOpen) {
      setSelectedDays(new Set());
    }
  }, [isOpen]);

  const toggleDay = useCallback(
    (day: string) => {
      if (day === sourceDay) return;
      setSelectedDays((prev) => {
        const next = new Set(prev);
        if (next.has(day)) {
          next.delete(day);
        } else {
          next.add(day);
        }
        return next;
      });
    },
    [sourceDay],
  );

  const isAllSelected = useMemo(
    () => selectableDays.length > 0 && selectableDays.every((d) => selectedDays.has(d)),
    [selectableDays, selectedDays],
  );

  const handleToggleSelectAll = useCallback(() => {
    setSelectedDays((prev) => {
      const allSelected = selectableDays.every((d) => prev.has(d));
      return allSelected ? new Set() : new Set(selectableDays);
    });
  }, [selectableDays]);

  const daysToSubmit = useMemo(() => Array.from(selectedDays), [selectedDays]);
  const confirmDisabled = daysToSubmit.length === 0 || isLoading;

  const handleConfirm = useCallback(async () => {
    if (confirmDisabled) return;

    try {
      const result = (await repeatMealOnSlotsAsync({
        meal_id: meal.id ?? "",
        mealTime,
        days: daysToSubmit,
        meal_name: meal.name ?? undefined,
        targetDate: targetDate ?? undefined,
      })) as Record<string, unknown> | undefined;

      const rawSkipped = result?.skipped_days;
      const skipped = Array.isArray(rawSkipped) ? (rawSkipped as string[]) : [];

      if (skipped.length > 0) {
        const addedCount = daysToSubmit.length - skipped.length;
        showInfo(
          `Repeated on ${addedCount} day${addedCount === 1 ? "" : "s"}. Couldn't add on ${skipped.join(", ")}.`,
        );
      } else {
        const n = daysToSubmit.length;
        showSuccess(`Meal repeated on ${n} day${n === 1 ? "" : "s"}.`);
      }
      onDismiss();
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to repeat meal. Please try again.");
    }
  }, [
    confirmDisabled,
    daysToSubmit,
    meal.id,
    meal.name,
    mealTime,
    onDismiss,
    repeatMealOnSlotsAsync,
    showError,
    showInfo,
    showSuccess,
    targetDate,
  ]);

  return (
    <RoundedModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      backdropDismiss={!isLoading}
      maxWidth="360px"
    >
      <div className="flex flex-col bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex flex-col items-center gap-1">
          <h2 className={cn(textVariants.title2, "text-center")}>Choose day(s)</h2>
          <p className={cn(textVariants.bodySingleLine, "text-center text-gray-500")}>
            Choose days to repeat meal
          </p>
        </div>

        <div className="flex flex-col">
          <DayRow label="Select All" checked={isAllSelected} onToggle={handleToggleSelectAll} />
          <div className="my-1 h-px w-full bg-gray-200" />
          {WEEKDAYS.map((day) => {
            const isSource = day === sourceDay;
            return (
              <DayRow
                key={day}
                label={WEEKDAY_LONG_LABELS[day]}
                checked={selectedDays.has(day)}
                disabled={isSource || isLoading}
                onToggle={() => toggleDay(day)}
              />
            );
          })}
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <FilledButton
            color="black"
            textCase="normal"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className="h-12 w-full !rounded-full !font-semibold"
          >
            {isLoading ? "Repeating…" : "Repeat"}
          </FilledButton>
          <FilledButton
            color="light-gray"
            textCase="normal"
            onClick={onDismiss}
            disabled={isLoading}
            className="h-12 w-full !rounded-full !font-semibold"
          >
            Cancel
          </FilledButton>
        </div>
      </div>
    </RoundedModal>
  );
});
